"use client";

import { useCallback, useMemo, useState } from "react";
import { useQueryWithStore } from "@/store/store.hooks";
import { selectCustomerPaymentMethodQuery } from "@/store/modules/customer-payment-methods/customer-payment-methods.selectors";
import { loadCustomerPaymentMethodsQuery } from "@/store/modules/customer-payment-methods/customer-payment-methods.helpers";
import { customerPaymentMethodsDefaultQueryConfig } from "@/store/modules/customer-payment-methods/customer-payment-methods-default-query";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import type { iCustomerPaymentMethod } from "@sdk/customer-payment-methods/customer-payment-method-model";
import { CustomerPaymentMethodCard } from "./customer-payment-method-card";
import { AddCustomerPaymentMethodCard } from "./add-customer-payment-method-card";
import { AddEditCustomerPaymentMethodModal } from "./add-edit-customer-payment-method-modal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Sparkles, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { WorkspaceSectionHeader } from "../../workspace-section-header";
import { QuickAddSelectionModal } from "../shared/quick-add-selection-modal";
import {
  buildCustomerPaymentMethodQuickAddOptions,
  buildCustomerPaymentMethodQuickAddPayload,
  useWorkspaceQuickAddContext,
} from "../shared/workspace-quick-add.helpers";

type ManageSectionLayout = "settings" | "wizard";

export function ManageCustomerPaymentMethodsSection({
  layout = "settings",
}: {
  layout?: ManageSectionLayout;
}) {
  const isWizard = layout === "wizard";
  const containerClassName = isWizard ? "space-y-4" : "space-y-6 px-4 pb-6";
  const [paymentMethodToEdit, setPaymentMethodToEdit] =
    useState<iCustomerPaymentMethod | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [paymentMethodToArchive, setPaymentMethodToArchive] =
    useState<iCustomerPaymentMethod | null>(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const quickAddContext = useWorkspaceQuickAddContext();

  const addEditModalOpen = isAddModalOpen || paymentMethodToEdit !== null;
  const closeAddEditModal = useCallback(() => {
    setIsAddModalOpen(false);
    setPaymentMethodToEdit(null);
  }, []);

  const { doAction: archivePaymentMethod, isProcessing: isArchiveProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (id: string) =>
          SDK.customerPaymentMethods.editById(id, { archived: true }),
        successMessage: "Payment method has been archived",
        failureMessage: "Could not archive payment method",
      }),
      []
    );

  const {
    state: queryState,
    isLoading,
    hasError,
    retry,
  } = useQueryWithStore(
    selectCustomerPaymentMethodQuery("all"),
    loadCustomerPaymentMethodsQuery(
      customerPaymentMethodsDefaultQueryConfig,
      "all"
    ),
    []
  );

  const allPaymentMethods = (queryState?.list ?? []) as iCustomerPaymentMethod[];
  const paymentMethods = useMemo(
    () => allPaymentMethods.filter((pm) => pm.archived !== true),
    [allPaymentMethods]
  );
  const archivedPaymentMethods = useMemo(
    () => allPaymentMethods.filter((pm) => pm.archived === true),
    [allPaymentMethods]
  );

  const refreshAll = useCallback(() => {
    retry();
  }, [retry]);

  const { doAction: createPaymentMethod, isProcessing: isQuickAddProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (details: Partial<iCustomerPaymentMethod>) =>
          SDK.customerPaymentMethods.create(details),
        throwError: true,
      }),
      []
    );

  const quickAddOptions = useMemo(
    () => buildCustomerPaymentMethodQuickAddOptions(paymentMethods, quickAddContext),
    [paymentMethods, quickAddContext]
  );

  const handleQuickAddPaymentMethods = useCallback(
    async (selectedIds: string[]) => {
      let createdCount = 0;
      for (const optionId of selectedIds) {
        const payload = buildCustomerPaymentMethodQuickAddPayload(
          optionId,
          quickAddContext
        );
        if (!payload) continue;
        await createPaymentMethod(payload);
        createdCount += 1;
      }
      refreshAll();
      toast.success(
        createdCount === 1
          ? "1 payment method added"
          : `${createdCount} payment methods added`
      );
    },
    [createPaymentMethod, quickAddContext, refreshAll]
  );

  const handleConfirmArchive = useCallback(async () => {
    if (!paymentMethodToArchive?.id) return;
    const result = await archivePaymentMethod(paymentMethodToArchive.id);
    if (result !== undefined) {
      refreshAll();
      setPaymentMethodToArchive(null);
    }
  }, [paymentMethodToArchive, archivePaymentMethod, refreshAll]);

  const handleEdit = useCallback((pm: iCustomerPaymentMethod) => {
    setIsAddModalOpen(false);
    setPaymentMethodToEdit(pm);
  }, []);

  if (isLoading) {
    return (
      <div className={containerClassName}>
        {!isWizard ? (
          <WorkspaceSectionHeader
            icon={Wallet}
            title="Customer Payment Methods"
            description="Add and manage payment methods used for customer payments."
          />
        ) : null}
        <div className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <div className="flex flex-wrap gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[88px] w-[300px] rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={containerClassName}>
        {!isWizard ? (
          <WorkspaceSectionHeader
            icon={Wallet}
            title="Customer Payment Methods"
            description="Add and manage payment methods used for customer payments."
          />
        ) : null}
        <p className="text-muted-foreground text-sm">
          Failed to load payment methods. Please try again.
        </p>
        <Button variant="outline" onClick={() => refreshAll()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <AddEditCustomerPaymentMethodModal
        open={addEditModalOpen}
        onOpenChange={(open) => !open && closeAddEditModal()}
        mode={paymentMethodToEdit ? "edit" : "create"}
        paymentMethod={paymentMethodToEdit ?? undefined}
        retry={refreshAll}
      />
      <QuickAddSelectionModal
        open={isQuickAddOpen}
        onOpenChange={setIsQuickAddOpen}
        title="Quick add customer payment methods"
        description="Select common customer payment methods to add with your workspace currency."
        options={quickAddOptions}
        confirmLabel="Add payment methods"
        onConfirm={handleQuickAddPaymentMethods}
      />
      <AlertDialog
        open={!!paymentMethodToArchive}
        onOpenChange={(open) => !open && setPaymentMethodToArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive payment method?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{paymentMethodToArchive?.label}&quot; will be hidden from
              new receipts. Existing receipts will keep this payment method.
              You can restore it from the archived section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmArchive();
              }}
              disabled={isArchiveProcessing}
            >
              {isArchiveProcessing ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {!isWizard ? (
        <WorkspaceSectionHeader
          icon={Wallet}
          title="Customer Payment Methods"
          description="Add and manage payment methods used for customer payments."
        />
      ) : null}

      <div className="space-y-4">
        {!isWizard ? (
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-muted-foreground" />
            <h4 className="font-medium">Active payment methods</h4>
          </div>
        ) : null}
        <div className={cn("flex flex-row flex-wrap gap-4", !isWizard && "mt-4")}>
          {paymentMethods.length === 0 ? (
            <div className="flex w-full flex-col items-start gap-3">
              <p className="text-sm text-muted-foreground">
                No active payment methods yet. Add one to get started.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsQuickAddOpen(true)}
                disabled={isQuickAddProcessing}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Quick add customer payment methods
              </Button>
            </div>
          ) : null}
          {paymentMethods.map((pm) => (
            <CustomerPaymentMethodCard
              key={pm.id}
              paymentMethod={pm}
              onEdit={() => handleEdit(pm)}
              onArchive={() => setPaymentMethodToArchive(pm)}
            />
          ))}
          <AddCustomerPaymentMethodCard
            label="Add payment method"
            onClick={() => {
              setPaymentMethodToEdit(null);
              setIsAddModalOpen(true);
            }}
          />
        </div>
      </div>

      {archivedPaymentMethods.length > 0 && (
        <Collapsible open={archivedOpen} onOpenChange={setArchivedOpen}>
          <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm font-medium hover:bg-muted/50">
            <span>
              Archived payment methods ({archivedPaymentMethods.length})
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                archivedOpen && "rotate-180"
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <div className="flex flex-row flex-wrap gap-4">
              {archivedPaymentMethods.map((pm) => (
                <CustomerPaymentMethodCard
                  key={pm.id}
                  paymentMethod={pm}
                  onEdit={() => handleEdit(pm)}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
